/* eslint-disable no-undef */
/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Download, ShareButton } from 'components/Download';

/**
 * @param {object} valueInfo - Información sobre el valor a mostrar (kWh o precio).
 * @param {object} generalReport - Reporte general de consumo.
 * @param {object} totalPerDay - Totales por día.
 * @param {number} year - Año.
 * @param {number} month - Mes actual.
 * @returns {JSX.Element} Componente que muestra los totales por día.
 *
 *  Este componente muestra una tabla con los totales diarios de consumo o costo,
 *  mínimos, máximos y promedios. Utiliza Material UI para la interfaz y
 *  react-perfect-scrollbar para el scroll.
 */
export default function TotalsPerDay({
  valueInfo,
  generalReport,
  totalPerDay,
  year,
  month,
}) {
  const intl = useIntl();
  const data = [];
  let obj;
  const tableRef = useRef();

  //Prepara los datos para mostrarlos en la tabla
  if (generalReport != undefined || generalReport != null) {
    Object.values(generalReport).forEach((element) => {
      element.forEach((element2) => {
        obj = {
          cost_kwh: element2.cost_kwh,
        };
        data.push(obj);
      });
    });
  }

  //Crea un array con los días del mes
  const [days, setDays] = useState([]);
  const localeNav = useSelector((state) => state.prefs.locale);

  useEffect(() => {
    const daysInMonth = year
      ? moment(`${year}${month}`).daysInMonth()
      : moment().daysInMonth();
    const currentMonth = moment().month();
    const currentYear = moment().year();
    const daysArray = [];
    moment.locale(localeNav);
    for (let i = 1; i <= daysInMonth; i++) {
      const date = moment({
        year: year ? year : currentYear,
        month: month ? month - 1 : currentMonth,
        day: i,
      });
      daysArray.push({
        day: i,
        weekday: date.format('ddd'),
      });
    }

    setDays(daysArray);
  }, [localeNav, month, year]);

  //Formatea los números como moneda USD
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  // Calculate daily totals
  const dailyTotals = [];
  const dailyMin = [];
  const dailyMax = [];
  const dailyAvg = [];
  //Calcula los totales diarios, mínimos, máximos y promedios

  if (generalReport) {
    for (let i = 1; i < Object.keys(generalReport).length + 1; i++) {
      let dailyTotal = 0;
      if (generalReport[i]) {
        generalReport[i].forEach((element) => {
          dailyTotal += element.cost_kwh;
        });
      }
      dailyTotals.push(dailyTotal);
    }
  }

  if (generalReport) {
    for (let i = 1; i < Object.keys(generalReport).length + 1; i++) {
      if (generalReport[i]) {
        const costKwhValues = generalReport[i].map(
          (element) => element.cost_kwh
        );
        dailyMin.push(Math.min(...costKwhValues).toFixed(2));
        dailyMax.push(Math.max(...costKwhValues).toFixed(2));
        dailyAvg.push(
          (
            costKwhValues.reduce((sum, value) => sum + value, 0) /
            costKwhValues.length
          ).toFixed(2)
        );
      }
    }
  }

  //Renderiza el componente
  return (
    <Box ref={tableRef}>
      <Paper sx={{ p: 2, overflowX: 'hidden', overflowY: 'auto' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {valueInfo.kwh ? (
            <Chip
              label={
                valueInfo.kwh
                  ? intl.formatMessage({
                      id: 'app.chargeSolar.board.totPerDay.title',
                    })
                  : intl.formatMessage({
                      id: 'app.chargeSolar.board.totPerDay.titlePrice',
                    })
              }
              sx={{
                fontWeight: 700,
                backgroundColor: '#730065',
                fontSize: '16px',
                color: 'white',
              }}
            />
          ) : (
            <Skeleton variant="text" width={200} height={50} />
          )}
          <Box display="flex" alignItems="center" gap={1}>
            <Download
              itemRef={tableRef}
              name={intl.formatMessage({
                id: 'app.chargeSolar.board.totPerDay.title',
              })}
            />
            <ShareButton
              itemRef={tableRef}
              name={intl.formatMessage({
                id: 'app.chargeSolar.board.totPerDay.title',
              })}
            />
          </Box>
        </Box>
        {generalReport && totalPerDay ? (
          <Box overflow="auto">
            <Table size="small" sx={{ width: '100%' }}>
              <TableHead>
                {generalReport != undefined || generalReport != null ? (
                  <TableRow>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: '0',
                        background: 'white',
                        zIndex: 1,
                        textAlign: 'center',
                        p: 1,
                      }}
                    >
                      {intl.formatMessage({
                        id: 'app.charge.board.totPerDay.label',
                      })}{' '}
                      {valueInfo.kwh ? 'kWh' : ''}
                    </TableCell>
                    {days.map((day, index) => (
                      <TableCell key={index} sx={{ textAlign: 'center', p: 1 }}>
                        {day.day}
                        <br />
                        {day.weekday.charAt(0).toUpperCase() +
                          day.weekday.slice(1, 3)}
                      </TableCell>
                    ))}
                  </TableRow>
                ) : null}
              </TableHead>
              <TableBody style={{ padding: '0 !important' }}>
                {generalReport != undefined || generalReport != null ? (
                  <TableRow sx={{ height: '0px' }}>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ textAlign: 'center', p: 1 }}
                    >
                      Total
                    </TableCell>
                    {Object.values(totalPerDay).map((totalRow, index) => (
                      <TableCell
                        key={index}
                        component="th"
                        scope="row"
                        sx={{
                          backgroundColor: valueInfo.kwh
                            ? totalRow.color_hm
                            : totalRow.color_cost,
                          textAlign: 'center',
                          p: 1,
                        }}
                      >
                        {valueInfo.kwh
                          ? totalRow.total
                          : `${formatter.format(totalRow.cost_kwh)}`}
                      </TableCell>
                    ))}
                  </TableRow>
                ) : null}
                {generalReport != undefined || generalReport != null ? (
                  <TableRow sx={{ height: '0px' }}>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        position: 'sticky',
                        left: '0',
                        background: 'white',
                        zIndex: 1,
                        textAlign: 'center',
                        p: 1,
                      }}
                    >
                      {intl.formatMessage({
                        id: 'app.charge.board.totPerDay.min',
                      })}
                    </TableCell>
                    {Object.values(totalPerDay).map((minRow, index) => (
                      <TableCell
                        component="th"
                        scope="row"
                        key={index}
                        sx={{ textAlign: 'center', p: 1 }}
                      >
                        {valueInfo.kwh ? minRow.min : `$${dailyMin[index]}`}
                      </TableCell>
                    ))}
                  </TableRow>
                ) : null}
                {generalReport != undefined || generalReport != null ? (
                  <TableRow sx={{ height: '0px' }}>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        position: 'sticky',
                        left: '0',
                        background: 'white',
                        zIndex: 1,
                        textAlign: 'center',
                        p: 1,
                      }}
                    >
                      {intl.formatMessage({
                        id: 'app.charge.board.totPerDay.max',
                      })}
                    </TableCell>
                    {Object.values(totalPerDay).map((maxRow, index) => (
                      <TableCell
                        component="th"
                        scope="row"
                        key={index}
                        sx={{ textAlign: 'center', p: 1 }}
                      >
                        {valueInfo.kwh ? maxRow.max : `$${dailyMax[index]}`}
                      </TableCell>
                    ))}
                  </TableRow>
                ) : null}
                {generalReport != undefined || generalReport != null ? (
                  <TableRow sx={{ height: '0px' }}>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        position: 'sticky',
                        left: '0',
                        background: 'white',
                        zIndex: 1,
                        textAlign: 'center',
                        p: 1,
                      }}
                    >
                      {intl.formatMessage({
                        id: 'app.charge.board.totPerDay.avrg',
                      })}
                    </TableCell>
                    {Object.values(totalPerDay).map((promRow, index) => (
                      <TableCell
                        component="th"
                        scope="row"
                        key={index}
                        sx={{ textAlign: 'center', p: 1 }}
                      >
                        {valueInfo.kwh ? promRow.prom : `$${dailyAvg[index]}`}
                      </TableCell>
                    ))}
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Skeleton width="100%" variant="rectangular" />
        )}
      </Paper>
    </Box>
  );
}
